import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { usePricing } from '../context/PricingContext';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import { formatMoney } from '../config/product';

const Checkout = () => {
  const { items, getTotalPrice, clearCart } = useCart();
  const { pricingRegion } = usePricing();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    customer_name: '',
    email: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API_BASE_URL}/api/order/create`, {
        ...formData,
        quantity: items[0]?.quantity || 1,
        pricing_region: items[0]?.pricingRegion || pricingRegion,
      });

      if (response.data.success) {
        clearCart();
        navigate('/confirmation', { 
          state: { order: response.data.order } 
        });
      }
    } catch (error) {
      console.error('Order creation error:', error);
      setError(error.response?.data?.error || 'Failed to create order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-white py-8 md:py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="border border-zinc-100 rounded-2xl bg-white shadow-sm p-8 md:p-12">
              <svg className="w-16 h-16 md:w-20 md:h-20 text-zinc-400 mx-auto mb-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l2.5 5m6-5v6a2 2 0 01-2 2H9a2 2 0 01-2-2v-6m8 0V9a2 2 0 00-2-2H9a2 2 0 00-2 2v4.01" />
              </svg>
              <h2 className="text-lg md:text-xl font-semibold text-zinc-900 mb-4">Your cart is empty</h2>
              <p className="text-sm text-zinc-600 font-light mb-8">Add some products to your cart before checkout.</p>
              <button
                onClick={() => navigate('/shop')}
                className="inline-flex items-center justify-center rounded-full bg-zinc-950 text-white px-6 md:px-8 py-3 min-h-[48px] text-sm font-medium tracking-wide hover:bg-zinc-800 transition-colors duration-300"
              >
                Continue Shopping
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white py-4 sm:py-6 md:py-8 lg:py-12">
      <div className="max-w-4xl mx-auto px-3 sm:px-4 lg:px-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-semibold tracking-tight text-zinc-900 mb-2">Checkout</h1>
          <p className="text-xs sm:text-sm text-zinc-600 font-light">Complete your order</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-4 sm:gap-6 md:gap-8">
          {/* Customer Information Form */}
          <div className="lg:col-span-1">
            <div className="border border-zinc-100 rounded-2xl bg-white shadow-sm p-4 sm:p-6 md:p-8">
              <h2 className="text-base md:text-lg font-semibold text-zinc-900 mb-4 sm:mb-6">Customer Information</h2>
              
              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                <div>
                  <label htmlFor="customer_name" className="block text-sm font-medium text-zinc-800 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="customer_name"
                    name="customer_name"
                    value={formData.customer_name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 min-h-[44px] border border-zinc-200 rounded-lg text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-300 focus:border-zinc-300 transition-colors"
                    placeholder="Enter your full name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-zinc-800 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 min-h-[44px] border border-zinc-200 rounded-lg text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-300 focus:border-zinc-300 transition-colors"
                    placeholder="Enter your email address"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-zinc-800 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 min-h-[44px] border border-zinc-200 rounded-lg text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-300 focus:border-zinc-300 transition-colors"
                    placeholder="Enter your phone number"
                  />
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex">
                      <svg className="w-5 h-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      <p className="text-red-600 text-sm">{error}</p>
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-zinc-950 text-white py-3 md:py-4 px-6 rounded-full min-h-[48px] text-sm md:text-base font-medium tracking-wide hover:bg-zinc-800 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                      </svg>
                      Place Order
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="border border-zinc-100 rounded-2xl bg-white shadow-sm p-4 sm:p-6 md:p-8">
              <h2 className="text-base md:text-lg font-semibold text-zinc-900 mb-4 sm:mb-6">Order Summary</h2>
              
              <div className="space-y-3 sm:space-y-4 mb-4 sm:mb-6">
                {items.map((item) => (
                  <div key={item.id} className="flex items-center space-x-4 p-4 bg-zinc-50 rounded-xl border border-zinc-100">
                    <img 
                      src={item.image} 
                      alt={item.name} 
                      className="w-12 h-12 md:w-16 md:h-16 object-contain rounded-lg"
                    />
                    <div className="flex-1">
                      <h3 className="text-xs md:text-sm font-medium text-zinc-900">{item.name}</h3>
                      <p className="text-xs text-zinc-600 font-light">Quantity: {item.quantity}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs md:text-sm font-semibold text-zinc-900">
                        {formatMoney(item.price * item.quantity, item.currency || 'USD')}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t border-zinc-100 pt-4">
                <div className="flex justify-between items-center">
                  <span className="text-base md:text-lg font-semibold text-zinc-900">Total</span>
                  <span className="text-xl md:text-2xl font-semibold text-zinc-900">
                    {items[0]
                      ? formatMoney(getTotalPrice(), items[0].currency || 'USD')
                      : formatMoney(getTotalPrice(), 'USD')}
                  </span>
                </div>
              </div>

              <div className="mt-6 p-4 bg-zinc-50 border border-zinc-100 rounded-xl">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-zinc-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <p className="text-sm text-zinc-800 font-medium">Order Confirmation</p>
                    <p className="text-xs text-zinc-600 font-light mt-1">
                      You will receive an email confirmation with QR code after placing your order.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
